import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

// The tavern's core layout primitive.
//
// Every card zone — shop, board, hand — is a horizontal rail on phones.
// Cards keep a thumb-sized footprint, overflow clips at the edges, and you
// swipe to reach the rest. This replaced two different failed approaches:
// squashing cards until their text was unreadable, and wrapping rows (which
// broke the single left-to-right line that combat resolves along).
//
// Two behaviours are load-bearing and easy to break:
//
//   1. `touch-action: pan-x` on the ITEMS (not here — see RailItem).
//      The browser claims horizontal movement for native scrolling and
//      hands everything else to dnd-kit. That's what lets swipe-to-browse
//      and drag-to-act share one finger without a long-press delay.
//
//   2. Native `overflow-x: auto` rather than a transform-based carousel.
//      dnd-kit auto-scrolls real scroll containers during a drag, so
//      off-screen drop targets stay reachable mid-drag for free.

export interface RailHandle {
  /** Scroll to the far end — used when a new card is appended. */
  scrollToEnd(smooth: boolean): void;
  /** Center the child at `index` in view — used by rail indicators. */
  scrollToIndex(index: number, smooth: boolean): void;
}

/** Which children are currently in view — feeds the rail indicators
    (dots / board minimap) so off-screen content is never invisible-by-
    omission. Indices are child positions in the scroller. */
export interface RailProgress {
  firstVisible: number;
  lastVisible: number;
  count: number;
}

export interface RailProps {
  children: React.ReactNode;
  /** Accessible name for the scroll region. */
  label?: string;
  /** Snap items to the start edge as you swipe. */
  snap?: boolean;
  /** Called when overflow state changes, so callers can show a hint. */
  onOverflowChange?: (overflow: { left: boolean; right: boolean }) => void;
  /** Called when the visible window changes — see RailProgress. */
  onProgressChange?: (progress: RailProgress) => void;
  /** Colour the edge fades blend into. Defaults to the asphalt backdrop. */
  fadeColor?: string;
  /** Applied to the scroller itself (alignment, padding, gap). */
  className?: string;
}

export const Rail = forwardRef<RailHandle, RailProps>(function Rail(
  {
    children,
    label,
    snap = false,
    onOverflowChange,
    onProgressChange,
    fadeColor = 'rgba(12,10,9,1)',
    className = '',
  },
  ref,
) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [overflow, setOverflow] = useState({ left: false, right: false });

  useImperativeHandle(ref, () => ({
    scrollToEnd(smooth: boolean) {
      const el = scrollerRef.current;
      if (!el) return;
      el.scrollTo({ left: el.scrollWidth, behavior: smooth ? 'smooth' : 'auto' });
    },
    scrollToIndex(index: number, smooth: boolean) {
      const el = scrollerRef.current;
      if (!el) return;
      const child = el.children[index] as HTMLElement | undefined;
      if (!child) return;
      // scrollTo, not scrollIntoView — the latter also scrolls ancestors,
      // which yanks the whole page when the rail sits low in the viewport.
      const left =
        child.offsetLeft - (el.clientWidth - child.clientWidth) / 2;
      el.scrollTo({ left, behavior: smooth ? 'smooth' : 'auto' });
    },
  }));

  // Last committed overflow, compared outside setState. The parent callback
  // must NOT fire inside the setOverflow updater: React runs updaters during
  // the render phase, so calling the parent's setState from there is a
  // setState-in-render violation ("Cannot update HandRail while rendering
  // Rail"). measure() is only ever invoked from effects and scroll/resize
  // listeners, so calling the callback directly here is always post-render.
  const overflowRef = useRef({ left: false, right: false });
  const progressRef = useRef<RailProgress | null>(null);

  const measure = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    // Tolerance of 2px absorbs sub-pixel rounding, which would otherwise
    // flicker the fades on every scroll event.
    const next = {
      left: el.scrollLeft > 2,
      right: el.scrollLeft < maxScroll - 2,
    };
    const prev = overflowRef.current;
    if (prev.left !== next.left || prev.right !== next.right) {
      overflowRef.current = next;
      setOverflow(next);
      onOverflowChange?.(next);
    }

    // Visible-window report for indicators. A child counts as visible when
    // at least ~half of a 8px sliver is inside the scroller box — edge
    // slivers under the fade gradient don't count as "you can see it".
    if (onProgressChange) {
      const box = el.getBoundingClientRect();
      const kids = Array.from(el.children) as HTMLElement[];
      let first = -1;
      let last = -1;
      kids.forEach((k, i) => {
        const r = k.getBoundingClientRect();
        const visible = r.right > box.left + 8 && r.left < box.right - 8;
        if (visible) {
          if (first === -1) first = i;
          last = i;
        }
      });
      const progress: RailProgress = {
        firstVisible: first,
        lastVisible: last,
        count: kids.length,
      };
      const prevP = progressRef.current;
      if (
        !prevP ||
        prevP.firstVisible !== progress.firstVisible ||
        prevP.lastVisible !== progress.lastVisible ||
        prevP.count !== progress.count
      ) {
        progressRef.current = progress;
        onProgressChange(progress);
      }
    }
  }, [onOverflowChange, onProgressChange]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    // Content changes (cards bought, sold, summoned) change overflow without
    // a scroll event, so observe the box as well.
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    for (const child of Array.from(el.children)) ro.observe(child);
    return () => {
      el.removeEventListener('scroll', measure);
      ro.disconnect();
    };
  }, [measure, children]);

  return (
    // min-h-0 + h-full is what gives rail children a definite height to
    // resolve `h-full` against. Without it, cards fall back to width-driven
    // sizing and overflow the zone.
    <div className="relative min-h-0 min-w-0 flex-1">
      <div
        ref={scrollerRef}
        role="list"
        aria-label={label}
        className={`flex h-full overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${
          snap ? 'snap-x snap-mandatory' : ''
        } ${className}`}
      >
        {children}
      </div>
      {overflow.left && <EdgeFade side="left" color={fadeColor} />}
      {overflow.right && <EdgeFade side="right" color={fadeColor} />}
    </div>
  );
});

function EdgeFade({ side, color }: { side: 'left' | 'right'; color: string }) {
  const transparent = color.replace(/rgba?\(([^)]+?)(,\s*[\d.]+)?\)/, (_m, rgb) => `rgba(${rgb},0)`);
  return (
    <div
      aria-hidden
      className={`pointer-events-none absolute inset-y-0 w-[24px] ${
        side === 'left' ? 'left-0' : 'right-0'
      }`}
      style={{
        background: `linear-gradient(to ${side === 'left' ? 'right' : 'left'}, ${color} 0%, ${transparent} 100%)`,
      }}
    />
  );
}

/**
 * Wrapper for a single rail entry.
 *
 * `touch-action: pan-x` is the reason the rail works: it must NOT be `none`
 * (which is what a fanned/static hand needed) or the browser routes every
 * gesture to dnd-kit and the rail stops scrolling entirely.
 */
export function railItemStyle(): React.CSSProperties {
  return { touchAction: 'pan-x' };
}
